<template>
  <SDialog v-model:open="modalVisible">
    <SDialogContent
      class="!w-auto !max-w-none"
      :show-close-button="false"
      @pointer-down-outside.prevent
      @open-auto-focus.prevent
    >
      <button
        type="button"
        class="absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus:outline-none"
        :aria-label="$t('button.close')"
        @click="closeModal"
      >
        <XIcon class="h-5 w-5" />
      </button>
      <SDialogHeader>
        <SDialogTitle class="flex items-center gap-3 text-xl font-semibold">
          <SvgIcon
            :iconContent="iconContent"
            :iconAlt="iconAlt"
            :iconStyle="iconStyle"
          />
          {{ $t("completeorder.print") }}
        </SDialogTitle>
      </SDialogHeader>

      <div class="flex items-stretch justify-center gap-3">
        <SButton
          v-if="showPrintTicketButton"
          type="button"
          variant="outline"
          class="only-buttons print-card"
          :disabled="printTicketLoading"
          data-cy="print-tickets-button"
          @click="printTickets2()"
        >
          <LoaderCircleIcon
            v-if="printTicketLoading"
            class="s-icon-spin spinner-icon"
          />
          <div v-else class="flex flex-col items-center justify-center gap-1">
            <span class="upper-text-label">{{ $t("completeorder.only") }}</span>
            <span class="lower-text-label">{{
              $t("completeorder.tickets")
            }}</span>
          </div>
        </SButton>

        <SButton
          type="button"
          variant="outline"
          class="only-buttons print-card"
          @click="componentPrintReceipt"
          data-cy="print-receipt-button"
        >
          <div class="flex flex-col items-center justify-center gap-1">
            <span class="upper-text-label">{{ $t("completeorder.only") }}</span>
            <span class="lower-text-label">{{
              $t("completeorder.receipts")
            }}</span>
          </div>
        </SButton>

        <SButton
          v-if="showPrintLabelButton"
          type="button"
          variant="outline"
          class="only-buttons print-card"
          @click="printLabels()"
        >
          <div class="flex flex-col items-center justify-center gap-1">
            <span class="upper-text-label">{{ $t("completeorder.only") }}</span>
            <span class="lower-text-label">{{
              $t("completeorder.labels")
            }}</span>
          </div>
        </SButton>

        <div
          v-if="showPrintTicketButton || showPrintLabelButton"
          class="vertical"
        ></div>

        <SButton
          v-if="showPrintTicketButton || showPrintLabelButton"
          type="button"
          variant="outline"
          class="both-buttons print-card"
          :disabled="showPrintTicketButton && printTicketLoading"
          @click="printAll"
        >
          <LoaderCircleIcon
            v-if="showPrintTicketButton && printTicketLoading"
            class="s-icon-spin spinner-icon"
          />
          <div v-else class="flex flex-col items-center justify-center gap-1">
            <span class="upper-text-label">{{
              $t("completeorder.print")
            }}</span>
            <div class="lower-text-label print-all">
              {{ $t("completeorder.all") }}
            </div>
          </div>
        </SButton>
      </div>
    </SDialogContent>
  </SDialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { ModalComponents, ModalStatus } from "@/constants/modal";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import printer from "@/assets/images/svg/printer.svg";
import { toggleModalVisible } from "@/utils/vue-helpers/modal";
import { IVatToPrint, TicketTypes } from "@/interfaces/receipts";
import PrinterConfigModule from "@/store/modules/printers-config";
import { LogLevel, LogService } from "@/services/logging";
import {
  printTicketsNative,
  printLabels,
  printBookingReceipt,
  printTicketsThruPrintNode,
} from "@/utils/vue-helpers/booking";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";
import { LoaderCircleIcon, XIcon } from "@smeetz/ui-icons";

export default defineComponent({
  name: ModalComponents.PrintTicketsAndReceipt,
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
    LoaderCircleIcon,
    XIcon,
  },
  async setup() {
    const iconStyle = "width: 20px; height: 27px;";
    const iconContent = printer;
    const iconAlt = "printer";
    const { t } = useI18n({});
    let vats = ref<IVatToPrint[]>([]);
    let loadingBookingInfo = ref(false);
    const modalVisible = dialogVisibleForStatus(
      ModalStatus.PrintTicketsAndReceipt
    );

    const showPrintLabelButton = computed(() => {
      return PrinterConfigModule.isLabelPrintingEnabled;
    });
    const showPrintTicketButton = computed(() => {
      return PrinterConfigModule.isTicketPrintingEnabled;
    });

    const printTicketLoading = ref(false);

    const closeModal = () => {
      toggleModalVisible(ModalStatus.Hidden);
    };

    async function componentPrintReceipt() {
      printBookingReceipt();
    }

    async function printTickets() {
      await printTicketsThruPrintNode();
    }

    // Print ticket from directly from native POS if a local printer is connected to device,
    // otherwise print from PrintNode remotely
    async function printTickets2() {
      printTicketLoading.value = true;
      try {
        // const printer = storageService.getLocalStorageItem(
        //   LocalStorageKeys.TicketPrinter
        // );
        // if (printer) {
        //   const printerReady = await isPrinterReady(printer);
        //   if (!printerReady) {
        //     NotificationService.showCustomError(t("error.printer-offline"));
        //     return;
        //   }
        //   await printTicketsNative(TicketTypes.Ticket, {
        //     fieldDiscount: this.$t("discount.discounted-price"),
        //   });
        //   NotificationService.showCustomSuccess(
        //     t("completeorder.print-tickets-success")
        //   );
        //   return;
        // }
        if (PrinterConfigModule.isNativeTicketPrintingEnabled) {
          await printTicketsNative();
          return;
        }

        // check if we need to print mini-tickets or regular tickets (based on the POS settings)
        const ticketType = PrinterConfigModule.ticketType;
        if (ticketType === TicketTypes.MiniTicket) {
          await printTicketsNative();
        } else {
          // print through PrintNode
          await printTickets();
        }
      } catch (error) {
        LogService.info(
          "Ticket Printing: Error when printing ticket",
          error,
          LogLevel.Error
        );
      } finally {
        printTicketLoading.value = false;
      }
    }

    async function printAll() {
      componentPrintReceipt();
      if (PrinterConfigModule.isTicketPrintingEnabled) {
        printTickets2();
      }
      if (PrinterConfigModule.isLabelPrintingEnabled) {
        printLabels();
      }
    }

    return {
      modalVisible,
      iconStyle,
      iconContent,
      iconAlt,
      t,
      closeModal,
      componentPrintReceipt,
      printTickets,
      printTickets2,
      loadingBookingInfo,
      vats,
      printLabels,
      showPrintLabelButton,
      showPrintTicketButton,
      printAll,
      printTicketLoading,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

.title-text {
  font-size: 22px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0em;
}

.only-buttons {
  background: $color-pale-yellow;
}

.both-buttons {
  background: $color-lemon-yellow;
}

.print-card {
  height: 142px;
  width: 141px;
  flex-shrink: 0;
  border-radius: 10px;
  padding: 0;
  border: $color-grey-3 1px solid;
}

.dialog-content {
  height: 395px;
  width: 549px;
  border-radius: 5px;
}

.upper-text-label {
  font-size: 10px;
  font-weight: 400;
  line-height: 13px;
  letter-spacing: 0em;
  text-align: center;
  color: $color-text-primary;
  margin-bottom: 8px;
}

.lower-text-label {
  font-size: 22px;
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0em;
  color: $color-text-primary;
  text-align: center;
  word-wrap: break-word;
}

.vertical {
  border-left: 1px dashed $color-grey-3;
  height: 141px;
  width: 1px;
  align-self: center;
}

.offset {
  margin-left: 10px;
}

.spinner-icon {
  font-size: 40px;
}
</style>
